import "./search-combobox.css";

import type {
	ComboBoxChangeEvent,
	ComboBoxFilterChangeEvent,
} from "@tql/ui-components-react/kendo/dropdowns";
import type { FieldRenderProps } from "@tql/ui-components-react/kendo/form";
import { FieldWrapper } from "@tql/ui-components-react/kendo/form";
import { Error, Hint, Label } from "@tql/ui-components-react/kendo/labels";
import { ComboBox } from "@tql/ui-components-react/tql/dropdowns";
import type { CSSProperties } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { ZodSchema } from "zod";

import type { Customer, LtlOwner } from "~/shared/schemas/customer-schema";
import type { ZipCode } from "~/shared/schemas/location-state-schema.ts";
import { getAndValidate } from "~/shared/services/api/fetch-client.ts";

type DataItem = Record<string, unknown>;

type FieldRenderPropsCombobox = {
	defaultValue?: DataItem;
	label: string;
	id: string;
	disabled?: boolean;
	hint: boolean;
	wrapperStyle: CSSProperties | undefined;
	hintDirection: "start" | "end" | undefined;
	textField: string;
	dataItemKey?: string;
	placeholder?: string;
	minSearchLength?: number;
	fetchItems?: (searchTerm: string) => Promise<DataItem[]>;
	schema: ZodSchema;
	route: string | ((searchTerm: string) => string);
	handleChange: (
		event: ComboBoxChangeEvent | { value: ZipCode } | { value: LtlOwner },
	) => void;
	onChange?: (event: ComboBoxChangeEvent) => void;
	onClear?: () => void;
	children?: React.ReactNode;
	setValueCleared?: (cleared: boolean) => void;
} & Partial<FieldRenderProps>;

export const SearchCombobox = (fieldRenderProps: FieldRenderPropsCombobox) => {
	const [items, setItems] = useState<DataItem[]>([]);
	const [loading, setLoading] = useState(false);
	const [filterText, setFilterText] = useState("");
	const [userInitiatedSearch, setUserInitiatedSearch] = useState(false); // Track if user initiated the search
	const [currentSearchTerm, setCurrentSearchTerm] = useState("");
	const [preservedSearchTerm, setPreservedSearchTerm] = useState(""); // Never cleared by external changes
	const [isUserClearing, setIsUserClearing] = useState(false); // Track when user is actively clearing
	const [userHasCleared, setUserHasCleared] = useState(false); // Track if user has cleared, persists longer
	const [clearKey, setClearKey] = useState(0); // Key to force ComboBox re-mount on clear
	const debounceRef = useRef<NodeJS.Timeout | null>(null);
	const clearingTimerRef = useRef<NodeJS.Timeout | null>(null);
	const comboBoxRef = useRef<HTMLDivElement>(null);
	const userMadeManualSelectionRef = useRef(false); // Track when user manually selects from dropdown
	const hasInitializedRef = useRef(false);
	const userHasClearedRef = useRef(false); // Persistent ref to track if user has cleared (survives re-mount)
	const [userStartedEditing, setUserStartedEditing] = useState(false);
	const [clearOperationInProgress, setClearOperationInProgress] =
		useState(false);

	const {
		textField,
		dataItemKey = "id",
		placeholder = `Type at least ${
			fieldRenderProps.minSearchLength ?? 3
		} characters to search...`,
		minSearchLength = 3,
		// fetchItems,
		validationMessage,
		touched,
		label,
		id,
		valid,
		disabled,
		hint,
		wrapperStyle,
		hintDirection,

		// eslint-disable-next-line @typescript-eslint/no-unused-vars, sonarjs/no-unused-vars -- asdf
		defaultValue: _defaultValue,
		// eslint-disable-next-line @typescript-eslint/no-unused-vars, sonarjs/no-unused-vars -- asdf
		onChange: _handleChange,
		// Use either value or defaultValue, prioritizing value for reactivity
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Value comes from form state with relaxed typing
		value: valueProp,
		route,
		schema,
		handleChange,
		onClear,
		setValueCleared,
		...others
	} = fieldRenderProps;

	const resolveRoute = useCallback(
		(searchTerm: string) =>
			typeof route === "function"
				? route(searchTerm)
				: `${route}${encodeURIComponent(searchTerm)}`,
		[route],
	);

	const processCustomerData = useCallback((data: Customer[]) => {
		for (const cust of data) {
			cust.nameWithId = `${cust.name} - ${cust.id}`;
		}
	}, []);

	const processPostalCodeData = useCallback((data: ZipCode[]) => {
		for (const postalCode of data) {
			postalCode.selectablePostalCode = `${postalCode.postalCode} - ${postalCode.city.name}, ${postalCode.city.state.name}, ${postalCode.city.state.countryCode}`;
		}
	}, []);

	const processDataItems = useCallback(
		(rawData: unknown[]) => {
			if (id.includes("customer")) {
				processCustomerData(rawData as Customer[]);
			}
			if (id.includes("PostalCode")) {
				processPostalCodeData(rawData as ZipCode[]);
			}
			return rawData;
		},
		[id, processCustomerData, processPostalCodeData],
	);

	const formatCustomerText = useCallback((item: DataItem) => {
		const name = item["name"];
		const customerId = item["id"];
		if (
			typeof name === "string" &&
			name.trim() &&
			(typeof customerId === "string" || typeof customerId === "number") &&
			String(customerId).trim()
		) {
			return `${name.trim()} - ${customerId}`;
		}
		return "";
	}, []);

	const formatPostalCodeText = useCallback((item: DataItem) => {
		const postalCode = item["postalCode"];
		const city = item["city"];

		if (!postalCode || typeof postalCode !== "string" || !postalCode.trim()) {
			return "";
		}

		if (!city || typeof city !== "object") {
			return postalCode.trim();
		}

		const cityName = (city as Record<string, unknown>)["name"];
		const state = (city as Record<string, unknown>)["state"];

		if (!cityName || typeof cityName !== "string" || !cityName.trim()) {
			return postalCode.trim();
		}

		if (!state || typeof state !== "object") {
			return postalCode.trim();
		}

		const stateName = (state as Record<string, unknown>)["name"];
		if (!stateName || typeof stateName !== "string" || !stateName.trim()) {
			return postalCode.trim();
		}

		const countryCode = (state as Record<string, unknown>)["countryCode"];
		const country =
			typeof countryCode === "string" && countryCode.trim()
				? countryCode
				: "US";

		return `${postalCode.trim()} - ${cityName.trim()}, ${stateName.trim()}, ${country}`;
	}, []);

	const getFormattedItemText = useCallback(
		(item: DataItem) => {
			// Priority 1: Check for processed formatted fields first
			const nameWithId = item["nameWithId"];
			if (typeof nameWithId === "string" && nameWithId.trim()) {
				return nameWithId;
			}

			const selectablePostalCode = item["selectablePostalCode"];
			if (
				typeof selectablePostalCode === "string" &&
				selectablePostalCode.trim()
			) {
				return selectablePostalCode;
			}

			// Priority 2: Generate formatted text for unprocessed data
			if (id.includes("customer")) {
				return formatCustomerText(item);
			}

			if (id.includes("PostalCode")) {
				return formatPostalCodeText(item);
			}

			// Priority 3: Check the specified textField
			const textFieldValue = item[textField];
			if (
				textField &&
				(typeof textFieldValue === "string" ||
					typeof textFieldValue === "number") &&
				String(textFieldValue).trim()
			) {
				return String(textFieldValue).trim();
			}

			return "";
		},
		[textField, id, formatCustomerText, formatPostalCodeText],
	);

	const handleAutoSelection = useCallback(
		(dataItems: unknown[]) => {
			if (dataItems.length > 0) {
				const firstItem = dataItems[0] as DataItem;

				// Get the formatted text and set it immediately for auto-selection
				const formattedText = getFormattedItemText(firstItem);
				setFilterText(formattedText);

				// Mark as manual selection to prevent further auto-selection attempts
				userMadeManualSelectionRef.current = true;

				// Clear the search state AFTER setting formatted text
				setUserInitiatedSearch(false);
				setCurrentSearchTerm("");
				setPreservedSearchTerm("");

				// Call the change handler to update form state
				const changeEvent: ComboBoxChangeEvent = {
					value: firstItem,
				} as ComboBoxChangeEvent;
				handleChange(changeEvent);
				setValueCleared?.(false);
			}
		},
		[getFormattedItemText, handleChange, setValueCleared],
	);

	const fetchData = useCallback(
		async (searchTerm: string, shouldAutoSelect = false) => {
			try {
				// For blur-triggered auto-selection, delay showing loading to avoid jarring flash
				if (shouldAutoSelect) {
					// Small delay before showing loading state to make auto-selection smoother
					setTimeout(() => setLoading(true), 100);
				} else {
					setLoading(true);
				}

				const results = await getAndValidate(
					resolveRoute(searchTerm),
					schema?.array(),
				);

				if (results.success) {
					const rawData = results.data ?? [];
					// Process data first to ensure formatted strings are available
					const processedData = processDataItems(rawData) as DataItem[];
					setItems(processedData);
					// Auto-select if requested (only on blur events)
					if (shouldAutoSelect && processedData.length > 0) {
						// Call handleAutoSelection immediately
						handleAutoSelection(processedData);
					}
				} else {
					setItems([]);
				}
			} catch (error) {
				console.error("Error fetching data:", error);
				setItems([]);
			} finally {
				setLoading(false);
			}
		},
		[resolveRoute, schema, processDataItems, handleAutoSelection],
	);

	const debouncedSearch = useCallback(
		(searchTerm: string, isUserInitiated = true) => {
			// Default to true since most searches are user-initiated
			if (debounceRef.current) {
				clearTimeout(debounceRef.current);
			}

			// User is typing - this is normal search behavior

			if (searchTerm && searchTerm.length >= minSearchLength) {
				// Prevent redundant searches if the term hasn't changed
				if (searchTerm === currentSearchTerm) {
					return;
				}

				setCurrentSearchTerm(searchTerm);
				setPreservedSearchTerm(searchTerm); // Always preserve user's search term
				setUserInitiatedSearch(isUserInitiated);
				// Don't set auto-select flags here - only fetch and show results
				// Auto-selection will be handled on blur event only

				debounceRef.current = setTimeout(() => {
					void fetchData(searchTerm);
				}, 300);
			} else {
				setItems([]);
				setCurrentSearchTerm("");
				setPreservedSearchTerm("");
				setUserInitiatedSearch(false);
			}
		},
		[minSearchLength, fetchData, currentSearchTerm],
	);

	const handleClearClick = useCallback(() => {
		setClearOperationInProgress(true);

		// Clear the filter text first to prevent showing old customer text during re-mount
		setFilterText("");
		setUserInitiatedSearch(false);
		setPreservedSearchTerm("");
		setCurrentSearchTerm("");
		setItems([]);
		userMadeManualSelectionRef.current = false;

		// Set clearing flags after clearing the text
		setIsUserClearing(true);
		setUserHasCleared(true);
		userHasClearedRef.current = true; // Set persistent ref
		setValueCleared?.(true);
		setClearKey((prev) => prev + 1); // Force ComboBox re-mount
		// Don't reset userStartedEditing on clear - user has started editing by clearing
		// setUserStartedEditing(false); // Reset editing flag on clear

		// Create a clear event and call handleChange to properly clear the form value
		const clearEvent: ComboBoxChangeEvent = {
			value: null,
		} as ComboBoxChangeEvent;

		if (onClear) {
			onClear();
		} else {
			// If no onClear handler, use the main change handler to clear the value
			handleChange(clearEvent);
		}

		// Reset clearing flag after operation completes
		setTimeout(() => {
			setIsUserClearing(false);
			setClearOperationInProgress(false);
		}, 150);
	}, [setValueCleared, onClear, handleChange]);

	const handleChangeInterceptor = useCallback(
		(event: ComboBoxChangeEvent) => {
			// If the value is null/undefined (clear action), call onClear instead of handleChange
			if (event.value === null || event.value === undefined) {
				// Set clearing flag to prevent repopulation
				setIsUserClearing(true);

				// Don't clear auto-select flags or state if we're in the middle of a search
				// This prevents external form resets from interfering with our auto-selection
				if (!preservedSearchTerm && !currentSearchTerm && !loading) {
					setFilterText(" ");
					setUserInitiatedSearch(false);
					setPreservedSearchTerm("");
					setCurrentSearchTerm("");
					setItems([]);
					userMadeManualSelectionRef.current = false;

					if (onClear) {
						onClear();
					} else {
						// If no onClear handler, use the main change handler to clear the value
						handleChange(event);
					}
				}

				// Reset clearing flag after operation completes
				setTimeout(() => {
					setIsUserClearing(false);
					setClearOperationInProgress(false);
				}, 150);
				return;
			}
			// Allow all valid selections - don't filter out based on search state

			// Mark that user made a manual selection - this prevents blur from triggering auto-selection
			userMadeManualSelectionRef.current = true;
			setUserStartedEditing(false); // Reset editing flag on selection
			setUserHasCleared(false); // Reset cleared flag on selection
			userHasClearedRef.current = false; // Reset persistent ref
			setValueCleared?.(false);

			// Clear user initiated search when user makes manual selection (real selection, not auto-selection)
			setUserInitiatedSearch(false);
			setPreservedSearchTerm("");
			// Set currentSearchTerm to the selected text to prevent redundant searches from onFilterChange
			const formattedText = getFormattedItemText(event.value as DataItem);
			setCurrentSearchTerm(formattedText);

			// Update filterText immediately with the selected item's formatted text
			setFilterText(formattedText);

			// Call the change handler

			// eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any -- Required for ComboBox compatibility
			handleChange(event as any);

			// Note: Manual selection flag will be cleared when this component loses focus
			// or when the user starts typing again in this component
			// Note: filterText will be updated by useEffect when state clears
		},
		[
			setValueCleared,
			getFormattedItemText,
			handleChange,
			preservedSearchTerm,
			currentSearchTerm,
			loading,
			onClear,
		],
	);

	const resetEditingFlag = useCallback(() => {
		setUserStartedEditing(false);
	}, []);

	const handleFilterChange = (event: ComboBoxFilterChangeEvent) => {
		const searchTerm = event.filter.value as string;
		const previousLength = filterText.length;

		// Skip search if the search term matches the current selection's formatted text
		// This prevents redundant searches after a manual selection
		if (valueProp && typeof valueProp === "object") {
			const formattedValue = getFormattedItemText(valueProp as DataItem);
			if (searchTerm === formattedValue) {
				setFilterText(searchTerm);
				return;
			}
		}

		// Mark that user has started editing
		if (!userStartedEditing && searchTerm.length > 0) {
			setUserStartedEditing(true);
			// Don't reset userHasCleared when user starts typing after clearing
			// setUserHasCleared(false); // Reset cleared flag when user starts typing
		}

		// Detect if user is clearing/backspacing (text getting shorter)
		if (searchTerm.length < previousLength) {
			setIsUserClearing(true);
			setUserHasCleared(true); // Set state and ref for clearing
			userHasClearedRef.current = true; // Set persistent ref
			setValueCleared?.(true);

			setFilterText(searchTerm); // Immediately update filterText to prevent showing old text

			// If text becomes empty, clear the form value
			if (searchTerm.length === 0) {
				const clearEvent: ComboBoxChangeEvent = {
					value: null,
				} as ComboBoxChangeEvent;
				handleChange(clearEvent);
			}

			// Reset flag after user stops typing
			clearingTimerRef.current = setTimeout(
				() => setIsUserClearing(false),
				300,
			);
		}

		// Reset editing flag after user stops typing
		if (debounceRef.current) {
			clearTimeout(debounceRef.current);
		}

		debounceRef.current = setTimeout(() => {
			resetEditingFlag();
		}, 1000); // Reset after 1 second of inactivity

		setFilterText(searchTerm);
		// Always treat filter changes as user-initiated
		debouncedSearch(searchTerm, true);
	};

	const handleBlur = useCallback(() => {
		// Call the original onBlur if provided (no parameters for direct DOM events)
		if (fieldRenderProps.onBlur) {
			fieldRenderProps.onBlur();
		}

		// If user just made a manual selection, ignore this blur event and clear the flag
		if (userMadeManualSelectionRef.current) {
			userMadeManualSelectionRef.current = false; // Clear flag immediately after checking
			return;
		}

		// Check conditions for auto-selection
		const hasFilterText = !!(filterText && filterText.length > 0);
		const meetsMinLength = filterText && filterText.length >= minSearchLength;

		// Skip auto-selection if the filter text matches the current selection's formatted text
		if (valueProp && typeof valueProp === "object") {
			const formattedValue = getFormattedItemText(valueProp as DataItem);
			if (filterText === formattedValue) {
				return;
			}
		}

		// Simple auto-selection: if user typed something and it meets minimum length, auto-select
		if (hasFilterText && meetsMinLength) {
			// Store the current filter text as user input
			setCurrentSearchTerm(filterText);
			setPreservedSearchTerm(filterText); // Also preserve it
			// Clear any existing debounce timeout to prevent conflicts
			if (debounceRef.current) {
				clearTimeout(debounceRef.current);
			}
			// Mark as user-initiated search
			setUserInitiatedSearch(true);
			// Make immediate API call for blur event with auto-selection enabled
			// This will cancel any ongoing request and start a new one
			void fetchData(filterText, true);
		}
	}, [
		fieldRenderProps,
		filterText,
		minSearchLength,
		fetchData,
		setCurrentSearchTerm,
		setPreservedSearchTerm,
		setUserInitiatedSearch,
		getFormattedItemText,
		valueProp,
	]);

	// Sync filterText when value changes externally (e.g., from parent component)
	// But preserve user input during active search operations
	useEffect(() => {
		// Don't update filterText if we have any ongoing search operations, user is clearing, or just made a selection
		if (
			userStartedEditing ||
			userHasClearedRef.current || // Check persistent ref instead of state
			clearOperationInProgress ||
			userInitiatedSearch ||
			loading ||
			currentSearchTerm ||
			isUserClearing ||
			userMadeManualSelectionRef.current
		) {
			return;
		}

		// Only update filterText from external value changes when we're not in the middle of a search operation
		if (valueProp && typeof valueProp === "object") {
			const formattedText = getFormattedItemText(valueProp as DataItem);
			// Only update if the formatted text is different from current filterText
			if (formattedText && formattedText !== filterText) {
				setFilterText(formattedText);
			}
		} else if (
			!valueProp &&
			!preservedSearchTerm &&
			!isUserClearing &&
			filterText !== ""
		) {
			// Only clear filterText if value is cleared externally AND user isn't actively searching or clearing
			setFilterText("");
		}
	}, [
		userStartedEditing,
		// userHasCleared, // Removed since we now use the ref
		valueProp,
		getFormattedItemText,
		userInitiatedSearch,
		loading,
		currentSearchTerm,
		preservedSearchTerm,
		isUserClearing,
		filterText,
		clearOperationInProgress,
		userHasCleared, // Keep in deps to trigger re-runs
	]);

	// Initialize filterText on mount if value is already present
	useEffect(() => {
		if (
			!hasInitializedRef.current &&
			valueProp &&
			typeof valueProp === "object" &&
			!filterText &&
			!isUserClearing &&
			!userStartedEditing &&
			!userHasClearedRef.current // Check persistent ref instead of state
		) {
			const formattedText = getFormattedItemText(valueProp as DataItem);
			if (formattedText) {
				setFilterText(formattedText);
				hasInitializedRef.current = true;
			}
		}
	}, [
		valueProp,
		getFormattedItemText,
		filterText,
		isUserClearing,
		userStartedEditing,
		// userHasCleared, // Removed since we now use the ref
		userHasCleared, // Keep in deps to trigger re-runs
	]);

	// Cleanup timeouts on unmount
	useEffect(() => {
		return () => {
			if (debounceRef.current) {
				clearTimeout(debounceRef.current);
			}
			if (clearingTimerRef.current) {
				clearTimeout(clearingTimerRef.current);
			}
		};
	}, []);

	const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
		if (event.key === " ") {
			// Stop propagation to prevent Kendo from using space as a selection key
			// This allows the space to be added to the input field instead of triggering selection
			event.stopPropagation();
		}
	}, []);

	// Add direct DOM event listeners as fallback if ComboBox doesn't support onBlur/onFocus
	useEffect(() => {
		const comboBoxElement = comboBoxRef.current;
		if (!comboBoxElement) return undefined;

		const handleDOMBlur = () => {
			handleBlur();
		};

		const handleDOMFocus = () => {
			// Clear manual selection flag when user focuses to type again
			// This allows auto-selection to work on subsequent blur events
			userMadeManualSelectionRef.current = false;
		};

		const handleDOMKeyDown = (e: KeyboardEvent) => {
			if (e.key === " ") {
				// Stop propagation to prevent Kendo from using space as a selection key
				e.stopPropagation();
			}
		};

		// Find the actual input element within the ComboBox
		const inputElement = comboBoxElement.querySelector("input");
		if (inputElement) {
			inputElement.addEventListener("blur", handleDOMBlur);
			inputElement.addEventListener("focus", handleDOMFocus);
			inputElement.addEventListener("keydown", handleDOMKeyDown);

			return () => {
				inputElement.removeEventListener("blur", handleDOMBlur);
				inputElement.removeEventListener("focus", handleDOMFocus);
				inputElement.removeEventListener("keydown", handleDOMKeyDown);
			};
		} else {
			console.error("Input element not found");
			return undefined;
		}
	}, [handleBlur, fieldRenderProps.name, fieldRenderProps.id]);

	const showValidationMessage: string | false | null =
		touched && validationMessage ? validationMessage : null;
	const showHint: boolean = !showValidationMessage && hint;
	const hintId: string = showHint ? `${id}_hint` : "";
	const errorId: string = showValidationMessage ? `${id}_error` : "";
	const labelId: string = label ? `${id}_label` : "";

	return (
		<FieldWrapper style={wrapperStyle}>
			<Label
				className={"k-form-label"}
				editorDisabled={disabled}
				editorId={id}
				editorValid={valid}
				id={labelId}
			>
				{label}
			</Label>
			<div ref={comboBoxRef} className={"k-form-field-wrap combobox-no-toggle"}>
				<ComboBox
					key={`combobox-${clearKey}`}
					allowCustom={false}
					clearButton={true}
					data={items}
					dataItemKey={dataItemKey}
					disabled={disabled}
					filter={valueProp ? filterText : undefined}
					filterable={true}
					id={id}
					loading={loading}
					onChange={handleChangeInterceptor}
					onClear={handleClearClick}
					onFilterChange={handleFilterChange}
					onKeyDown={handleKeyDown}
					placeholder={placeholder}
					suggest={true}
					textField={textField}
					valid={valid}
					{...others}
				/>
				{showHint ? (
					<Hint direction={hintDirection} id={hintId}>
						{hint}
					</Hint>
				) : undefined}
				{showValidationMessage ? (
					<Error id={errorId}>{validationMessage}</Error>
				) : undefined}
			</div>
		</FieldWrapper>
	);
};
